// ============================================================
// Analysis Service - "Golden Thread" AIAG-VDA Data Pipeline
// ============================================================
// Central service connecting Structure → Function → Failure → Risk
// Project-scoped, RBAC-aware, with event-based synchronization
// ============================================================

import { auth } from '../config/firebase';
import { authenticatedFetchJSON } from '../utils/authenticatedFetch';
import {
    ProcessStep,
    ProcessFunction,
    ProductRequirement,
    PfmeaItem,
    CauseCategory,
    ControlPlanItem,
    WorkInstruction,
    WorkElement,
} from '../types';

// ============================================================
// TYPES - Failure Chain (AIAG-VDA Step 4)
// ============================================================

export interface FailureChain {
    id: string;
    projectName: string;
    // Source Links (Golden Thread)
    processStepId: string;           // From Structure Analysis
    processFunctionId: string;       // From Function Analysis
    // Failure Analysis (Step 4)
    failureMode: string;             // Anti-function
    failureEffectLocal?: string;
    failureEffectNext?: string;
    failureEffectEnd: string;
    failureCause: string;
    causeCategory: CauseCategory;    // 4M Element
    // Status
    status: 'draft' | 'linked';      // 'linked' when synced to PFMEA
    linkedPfmeaId?: string;
    createdAt: string;
    updatedAt: string;
}

export interface AnalysisSnapshot {
    projectName: string;
    processSteps: ProcessStep[];
    processFunctions: ProcessFunction[];
    productRequirements: ProductRequirement[];
    failureChains: FailureChain[];
    workElements: WorkElement[]; // Added for AIAG-VDA Step 2
    lastSyncedAt: string;
}

// ============================================================
// LOCAL STORAGE KEYS (Fallback)
// ============================================================

const STORAGE_KEYS = {
    PROCESS_STEPS: 'magnus_analysis_process_steps',
    PROCESS_FUNCTIONS: 'magnus_analysis_functions',
    PRODUCT_REQUIREMENTS: 'magnus_analysis_requirements',
    FAILURE_CHAINS: 'magnus_analysis_failure_chains',
};

// ============================================================
// HELPER FUNCTIONS - Low Complexity
// ============================================================

function getStorageKey(key: string, projectName: string): string {
    return `${key}_${projectName.replace(/\s+/g, '_')}`;
}

function loadFromStorage<T>(key: string, projectName: string): T[] {
    try {
        const stored = localStorage.getItem(getStorageKey(key, projectName));
        return stored ? JSON.parse(stored) : [];
    } catch {
        return [];
    }
}

function saveToStorage<T>(key: string, projectName: string, data: T[]): void {
    localStorage.setItem(getStorageKey(key, projectName), JSON.stringify(data));
}

function generateId(prefix: string): string {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
}

// ============================================================
// PROCESS STEPS (Structure Analysis - Step 2)
// ============================================================

/**
 * Extract process steps from a React Flow diagram
 * Converts visual nodes into structured ProcessStep data
 */
export function extractProcessStepsFromFlow(
    nodes: any[],
    _projectName: string
): ProcessStep[] {
    return nodes
        .filter(node => node.type === 'processFlowNode' || node.data?.stepNumber)
        .map((node, index) => ({
            id: node.id,
            stepNumber: node.data?.stepNumber || `OP-${String((index + 1) * 10).padStart(3, '0')}`,
            stepName: node.data?.label || node.data?.stepName || 'Unnamed Step',
            stepType: node.data?.type || 'operation',
            description: node.data?.description || '',
            equipment: node.data?.equipment,
            operator: node.data?.operator,
            cycleTime: node.data?.cycleTime,
            specialCharacteristics: node.data?.specialCharacteristics,
            position: node.position,
            processElement: node.data?.element4M as CauseCategory,
            characteristic: node.data?.characteristic,
            specification: node.data?.specification,
            controlMethod: node.data?.controlMethod,
            sampleSize: node.data?.sampleSize,
            sampleFrequency: node.data?.sampleFrequency,
        }));
}

/**
 * Get process steps for a project
 */
export async function getProcessSteps(projectName: string): Promise<ProcessStep[]> {
    try {
        const data = await authenticatedFetchJSON<{ steps: ProcessStep[] }>(
            `/api/analysis/steps?project=${encodeURIComponent(projectName)}`
        );
        return data.steps || [];
    } catch {
        return loadFromStorage<ProcessStep>(STORAGE_KEYS.PROCESS_STEPS, projectName);
    }
}

/**
 * Save process steps for a project
 */
export async function saveProcessSteps(
    projectName: string,
    steps: ProcessStep[]
): Promise<void> {
    try {
        await authenticatedFetchJSON<void>('/api/analysis/steps', {
            method: 'POST',
            body: JSON.stringify({ projectName, steps }),
        });
    } catch {
        saveToStorage(STORAGE_KEYS.PROCESS_STEPS, projectName, steps);
    }
}

// ============================================================
// PRODUCT REQUIREMENTS (Step 1/3)
// ============================================================

/**
 * Get product requirements for a project
 */
export async function getProductRequirements(projectName: string): Promise<ProductRequirement[]> {
    try {
        const data = await authenticatedFetchJSON<{ requirements: ProductRequirement[] }>(
            `/api/analysis/requirements?project=${encodeURIComponent(projectName)}`
        );
        return data.requirements || [];
    } catch {
        return loadFromStorage<ProductRequirement>(STORAGE_KEYS.PRODUCT_REQUIREMENTS, projectName);
    }
}

/**
 * Save product requirements for a project
 */
export async function saveProductRequirements(
    projectName: string,
    requirements: ProductRequirement[]
): Promise<void> {
    try {
        await authenticatedFetchJSON<void>('/api/analysis/requirements', {
            method: 'POST',
            body: JSON.stringify({ projectName, requirements }),
        });
    } catch {
        saveToStorage(STORAGE_KEYS.PRODUCT_REQUIREMENTS, projectName, requirements);
    }
}

// ============================================================
// PROCESS FUNCTIONS (Function Analysis - Step 3)
// ============================================================

/**
 * Create a function linking a process step to a requirement
 */
export function createProcessFunction(
    stepId: string,
    requirementId: string,
    description: string
): ProcessFunction {
    return {
        id: generateId('func'),
        processStepId: stepId,
        functionDescription: description,
        productRequirementId: requirementId,
    };
}

/**
 * Get process functions for a project
 */
export async function getProcessFunctions(projectName: string): Promise<ProcessFunction[]> {
    try {
        const data = await authenticatedFetchJSON<{ functions: ProcessFunction[] }>(
            `/api/analysis/functions?project=${encodeURIComponent(projectName)}`
        );
        return data.functions || [];
    } catch {
        return loadFromStorage<ProcessFunction>(STORAGE_KEYS.PROCESS_FUNCTIONS, projectName);
    }
}

/**
 * Save process functions for a project
 */
export async function saveProcessFunctions(
    projectName: string,
    functions: ProcessFunction[]
): Promise<void> {
    try {
        await authenticatedFetchJSON<void>('/api/analysis/functions', {
            method: 'POST',
            body: JSON.stringify({ projectName, functions }),
        });
    } catch {
        saveToStorage(STORAGE_KEYS.PROCESS_FUNCTIONS, projectName, functions);
    }
}

// ============================================================
// FAILURE CHAINS (Failure Analysis - Step 4)
// ============================================================

/**
 * Create a failure chain from a process function
 */
export function createFailureChain(
    projectName: string,
    processStepId: string,
    processFunctionId: string,
    causeCategory: CauseCategory
): FailureChain {
    const now = new Date().toISOString();
    return {
        id: generateId('fc'),
        projectName,
        processStepId,
        processFunctionId,
        failureMode: '',
        failureEffectEnd: '',
        failureCause: '',
        causeCategory,
        status: 'draft',
        createdAt: now,
        updatedAt: now,
    };
}

/**
 * Get failure chains for a project
 */
export async function getFailureChains(projectName: string): Promise<FailureChain[]> {
    try {
        const data = await authenticatedFetchJSON<{ chains: FailureChain[] }>(
            `/api/analysis/failure-chains?project=${encodeURIComponent(projectName)}`
        );
        return data.chains || [];
    } catch {
        return loadFromStorage<FailureChain>(STORAGE_KEYS.FAILURE_CHAINS, projectName);
    }
}

/**
 * Save failure chains for a project
 */
export async function saveFailureChains(
    projectName: string,
    chains: FailureChain[]
): Promise<void> {
    try {
        await authenticatedFetchJSON<void>('/api/analysis/failure-chains', {
            method: 'POST',
            body: JSON.stringify({ projectName, chains }),
        });
    } catch {
        saveToStorage(STORAGE_KEYS.FAILURE_CHAINS, projectName, chains);
    }
}

// ============================================================
// SYNC TO PFMEA (Risk Analysis - Step 5)
// ============================================================

/**
 * Convert a failure chain to a PFMEA item shell
 * User then adds S/O/D ratings and controls
 */
export function failureChainToPfmeaItem(
    chain: FailureChain,
    step: ProcessStep,
    func: ProcessFunction
): Partial<PfmeaItem> {
    return {
        id: generateId('pfmea'),
        projectName: chain.projectName,
        standard: 'AIAG_VDA',
        processStepNumber: step.stepNumber,
        processStepName: step.stepName,
        processElement: chain.causeCategory,
        processFunction: func.functionDescription,
        failureMode: chain.failureMode,
        failureEffectLocal: chain.failureEffectLocal,
        failureEffectNext: chain.failureEffectNext,
        failureEffectEnd: chain.failureEffectEnd,
        failureCause: chain.failureCause,
        linkedProcessStepId: step.id,
        linkedFunctionId: func.id,
        // User must fill these:
        severity: 1,
        occurrence: 1,
        detection: 1,
        preventionControls: [],
        detectionControls: [],
        recommendedActions: [],
        status: 'draft',
        createdBy: auth.currentUser?.email || 'unknown',
        createdAt: new Date(),
        updatedAt: new Date(),
    };
}

/**
 * Sync all unlinked failure chains to PFMEA items
 */
export function syncFailureChainsToPfmea(
    chains: FailureChain[],
    steps: ProcessStep[],
    functions: ProcessFunction[],
    existingPfmeaItems: PfmeaItem[]
): { newItems: Partial<PfmeaItem>[]; updatedChains: FailureChain[] } {
    const newItems: Partial<PfmeaItem>[] = [];
    const updatedChains: FailureChain[] = [];

    // Build lookup maps
    const stepMap = new Map(steps.map(s => [s.id, s]));
    const funcMap = new Map(functions.map(f => [f.id, f]));
    const existingLinkedChainIds = new Set(
        existingPfmeaItems.filter(p => p.linkedFunctionId).map(p => p.linkedFunctionId)
    );

    for (const chain of chains) {
        // Skip if already linked or incomplete
        if (chain.status === 'linked' || !chain.failureMode || !chain.failureEffectEnd) {
            updatedChains.push(chain);
            continue;
        }

        // Skip if already exists in PFMEA
        const step = stepMap.get(chain.processStepId);
        const func = funcMap.get(chain.processFunctionId);
        if (!step || !func) {
            updatedChains.push(chain);
            continue;
        }

        // Check if this chain's function is already linked
        if (existingLinkedChainIds.has(func.id)) {
            updatedChains.push({ ...chain, status: 'linked' });
            continue;
        }

        // Create new PFMEA item
        const pfmeaItem = failureChainToPfmeaItem(chain, step, func);
        newItems.push(pfmeaItem);
        updatedChains.push({
            ...chain,
            status: 'linked',
            linkedPfmeaId: pfmeaItem.id,
            updatedAt: new Date().toISOString(),
        });
    }

    return { newItems, updatedChains };
}

// ============================================================
// CONTROL PLAN SYNC (Documentation - Step 7)
// ============================================================

/**
 * Generate control plan items from high-risk PFMEA items
 */
export function generateControlPlanFromPfmea(
    pfmeaItems: PfmeaItem[]
): ControlPlanItem[] {
    const highRiskItems = pfmeaItems.filter(item =>
        item.actionPriority === 'H' || item.actionPriority === 'M' || item.severity >= 9
    );

    function getSampleSize(severity: number) {
        if (severity >= 9) return { size: '100%', freq: 'Every piece' };
        if (severity >= 7) return { size: 'n=5', freq: 'Every hour' };
        if (severity >= 5) return { size: 'n=5', freq: 'Every shift' };
        return { size: 'n=2', freq: 'First/Last piece' };
    }

    return highRiskItems.flatMap((pfmea, index) => {
        const items: ControlPlanItem[] = [];
        const sampling = getSampleSize(pfmea.severity);

        // Create item for each detection control
        if (pfmea.detectionControls && pfmea.detectionControls.length > 0) {
            pfmea.detectionControls.forEach((control, ctrlIdx) => {
                items.push({
                    id: `cp-${Date.now()}-${index}-${ctrlIdx}`,
                    processStepNumber: pfmea.processStepNumber,
                    processStepName: pfmea.processStepName,
                    characteristic: pfmea.failureMode,
                    characteristicType: 'process',
                    specialCharacteristic: pfmea.specialCharacteristic || 'none',
                    specification: pfmea.requirement || '',
                    evaluationTechnique: control,
                    sampleSize: sampling.size,
                    sampleFrequency: sampling.freq,
                    controlMethod: control,
                    reactionPlan: 'Stop, notify supervisor, segregate suspect parts',
                    linkedPfmeaId: pfmea.id,
                });
            });
        } else {
            // Default item if no detection controls defined
            items.push({
                id: `cp-${Date.now()}-${index}`,
                processStepNumber: pfmea.processStepNumber,
                processStepName: pfmea.processStepName,
                characteristic: pfmea.failureMode,
                characteristicType: 'process',
                specialCharacteristic: pfmea.specialCharacteristic || 'none',
                specification: pfmea.requirement || '',
                evaluationTechnique: 'Visual inspection',
                sampleSize: sampling.size,
                sampleFrequency: sampling.freq,
                controlMethod: 'Operator inspection',
                reactionPlan: 'Stop, notify supervisor, segregate suspect parts',
                linkedPfmeaId: pfmea.id,
            });
        }

        return items;
    });
}

// ============================================================
// WORK INSTRUCTIONS (Step 7)
// ============================================================

const WORK_INSTRUCTIONS_KEY = 'magnus_work_instructions';

export async function getWorkInstructions(projectName: string): Promise<WorkInstruction[]> {
    try {
        const data = await authenticatedFetchJSON<{ instructions: WorkInstruction[] }>(
            `/api/analysis/instructions?project=${encodeURIComponent(projectName)}`
        );
        return data.instructions || [];
    } catch {
        return loadFromStorage<WorkInstruction>(WORK_INSTRUCTIONS_KEY, projectName);
    }
}

export async function saveWorkInstructions(
    projectName: string,
    instructions: WorkInstruction[]
): Promise<void> {
    try {
        await authenticatedFetchJSON<void>('/api/analysis/instructions', {
            method: 'POST',
            body: JSON.stringify({ projectName, instructions }),
        });
    } catch {
        saveToStorage(WORK_INSTRUCTIONS_KEY, projectName, instructions);
    }
}

// ============================================================
// PROJECT ACCESS MANAGEMENT (RBAC)
// ============================================================

export interface ProjectMember {
    uid: string;
    email: string;
    displayName: string;
    role: 'viewer' | 'editor' | 'admin';
    addedAt: string;
    addedBy: string;
}

const PROJECT_MEMBERS_KEY = 'magnus_project_members';

/**
 * Get project members
 */
export async function getProjectMembers(projectName: string): Promise<ProjectMember[]> {
    try {
        const data = await authenticatedFetchJSON<{ members: ProjectMember[] }>(
            `/api/projects/${encodeURIComponent(projectName)}/members`
        );
        return data.members || [];
    } catch {
        return loadFromStorage<ProjectMember>(PROJECT_MEMBERS_KEY, projectName);
    }
}

/**
 * Add a member to a project
 */
export async function addProjectMember(
    projectName: string,
    email: string,
    role: ProjectMember['role']
): Promise<ProjectMember> {
    const member: ProjectMember = {
        uid: '', // Server or lookup will fill
        email,
        displayName: email.split('@')[0],
        role,
        addedAt: new Date().toISOString(),
        addedBy: auth.currentUser?.email || 'unknown',
    };

    try {
        return await authenticatedFetchJSON<ProjectMember>(
            `/api/projects/${encodeURIComponent(projectName)}/members`,
            { method: 'POST', body: JSON.stringify(member) }
        );
    } catch {
        // Fallback: save to local storage
        const members = loadFromStorage<ProjectMember>(PROJECT_MEMBERS_KEY, projectName);
        members.push(member);
        saveToStorage(PROJECT_MEMBERS_KEY, projectName, members);
        return member;
    }
}

/**
 * Remove a member from a project
 */
export async function removeProjectMember(
    projectName: string,
    uid: string
): Promise<void> {
    try {
        await authenticatedFetchJSON<void>(
            `/api/projects/${encodeURIComponent(projectName)}/members/${uid}`,
            { method: 'DELETE' }
        );
    } catch {
        const members = loadFromStorage<ProjectMember>(PROJECT_MEMBERS_KEY, projectName);
        const filtered = members.filter(m => m.uid !== uid);
        saveToStorage(PROJECT_MEMBERS_KEY, projectName, filtered);
    }
}

/**
 * Check if current user has access to project
 */
export async function checkProjectAccess(
    projectName: string,
    requiredRole: ProjectMember['role'] = 'viewer'
): Promise<boolean> {
    const currentUser = auth.currentUser;
    if (!currentUser) return false;

    const members = await getProjectMembers(projectName);
    const member = members.find(m => m.email === currentUser.email || m.uid === currentUser.uid);

    if (!member) return false;

    const roleHierarchy = { viewer: 1, editor: 2, admin: 3 };
    return roleHierarchy[member.role] >= roleHierarchy[requiredRole];
}

// ============================================================
// FULL ANALYSIS SNAPSHOT (For Reports/Export)
// ============================================================

/**
 * Load complete analysis snapshot for a project
 */
export async function loadAnalysisSnapshot(projectName: string): Promise<AnalysisSnapshot> {
    const [processSteps, processFunctions, productRequirements, failureChains, workElements] = await Promise.all([
        getProcessSteps(projectName),
        getProcessFunctions(projectName),
        getProductRequirements(projectName),
        getFailureChains(projectName),
        getWorkElements(projectName),
    ]);

    return {
        projectName,
        processSteps,
        processFunctions,
        productRequirements,
        failureChains,
        workElements,
        lastSyncedAt: new Date().toISOString(),
    };
}

// ============================================================
// WORK ELEMENTS (Structure Analysis - Step 2)
// ============================================================

/**
 * Get work elements for a project
 */
export async function getWorkElements(projectName: string): Promise<WorkElement[]> {
    try {
        const data = await authenticatedFetchJSON<{ workElements: WorkElement[] }>(
            `/api/analysis/work-elements?project=${encodeURIComponent(projectName)}`
        );
        return data.workElements || [];
    } catch {
        // Fallback: load from local storage
        return loadFromStorage<WorkElement>('magnus_analysis_work_elements', projectName);
    }
}

/**
 * Save work elements for a project
 */
export async function saveWorkElements(
    projectName: string,
    workElements: WorkElement[]
): Promise<void> {
    try {
        await authenticatedFetchJSON<void>('/api/analysis/work-elements', {
            method: 'POST',
            body: JSON.stringify({ projectName, workElements }),
        });
    } catch {
        // Fallback: save to local storage
        saveToStorage('magnus_analysis_work_elements', projectName, workElements);
    }
}

export default {
    // Structure Analysis (Step 2)
    extractProcessStepsFromFlow,
    getProcessSteps,
    saveProcessSteps,
    getWorkElements,
    saveWorkElements,
    // Function Analysis (Step 3)
    createProcessFunction,
    getProcessFunctions,
    saveProcessFunctions,
    // Product Requirements
    getProductRequirements,
    saveProductRequirements,
    // Failure Analysis (Step 4)
    createFailureChain,
    getFailureChains,
    saveFailureChains,
    // Risk Analysis Sync (Step 5)
    failureChainToPfmeaItem,
    syncFailureChainsToPfmea,
    // Control Plan (Step 7)
    generateControlPlanFromPfmea,
    // RBAC
    getProjectMembers,
    addProjectMember,
    removeProjectMember,
    checkProjectAccess,
    // Full Snapshot
    loadAnalysisSnapshot,
};
